// components/AideGlobale.jsx
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  X, HelpCircle, BookOpen, Settings, CheckCircle,
  ChevronDown, ChevronUp, Info, Fingerprint, FileText,
  Users, BarChart2, ArrowRight, GraduationCap
} from 'lucide-react';

const COLOR = '#2d6a7f';
const COLOR_LIGHT = '#2d6a7f12';
const COLOR_BORDER = '#2d6a7f30';

const AccordionSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: COLOR }} />
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLOR + '15' }}>
            <Icon size={15} style={{ color: COLOR }} />
          </div>
          <span className="text-sm font-bold text-gray-800">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={15} className="text-gray-400" /> : <ChevronDown size={15} className="text-gray-400" />}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

const ConfigStep = ({ num, title, desc, module }) => (
  <div className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/30">
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-xs"
      style={{ backgroundColor: COLOR }}
    >
      {num}
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-0.5">
        <p className="text-xs font-semibold text-gray-900">{title}</p>
        <span
          className="text-xs px-2 py-0.5 rounded font-medium"
          style={{ backgroundColor: COLOR + '15', color: COLOR }}
        >
          {module}
        </span>
      </div>
      <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
    </div>
  </div>
);

const AideGlobale = ({ onClose }) => {
  return ReactDOM.createPortal(
    <>
      <div className="fixed inset-0 bg-black/50" style={{ zIndex: 9998 }} />
      <div
        className="fixed right-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col"
        style={{ zIndex: 9999, animation: 'hpSlideInRight 0.25s ease-out both', top: 0, bottom: 0, height: '100vh' }}
      >
        {/* HEADER */}
        <div
          className="flex-shrink-0 px-6 py-5 flex items-center justify-between"
          style={{ background: `linear-gradient(135deg, ${COLOR} 0%, #448696 100%)` }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <HelpCircle size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white tracking-wide">Centre d'aide</h2>
              <p className="text-xs text-white/75 mt-0.5">Démarrage et ressources disponibles</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white">
            <X size={18} />
          </button>
        </div>

        {/* INTRO */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-gray-100 bg-white">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
            <Info size={14} className="text-gray-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 leading-relaxed">
              Ce centre d'aide vous accompagne dans la prise en main de l'application. Pour aller plus loin, chaque module complexe dispose de son propre guide accessible directement depuis son interface.
            </p>
          </div>
        </div>

        {/* CONTENU */}
        <div className="flex-1 overflow-y-auto modal-scroll p-5 space-y-3" style={{ backgroundColor: '#fdfcf5' }}>

          {/* Section 1 — Configuration initiale */}
          <AccordionSection title="Configuration initiale - par où commencer ?" icon={Settings} defaultOpen={true}>
            <div className="px-4 py-3 rounded-lg bg-amber-50 border border-amber-100 mb-1">
              <p className="text-xs text-amber-700 leading-relaxed">
                Avant d'utiliser HoraPilot au quotidien, ces étapes de configuration sont indispensables. Suivez-les dans l'ordre recommandé pour une mise en place optimale.
              </p>
            </div>
            <ConfigStep
              num="1"
              title="Configurer les départements"
              desc="Créez les départements de votre organisation avant d'ajouter des employés. Vous pouvez créer vos propres départements ou utiliser les départements par défaut proposés par HoraPilot via le bouton Générer - disponible uniquement si aucun département n'existe encore."
              module="Paramètres"
            />
            <ConfigStep
              num="2"
              title="Paramétrer le système de pointage"
              desc="Définissez les horaires de travail, les règles de ponctualité, le mode de pause et le mode de pointage. Ces paramètres s'appliquent à l'ensemble de votre organisation."
              module="Paramètres"
            />
            <ConfigStep
              num="3"
              title="Créer les comptes utilisateurs"
              desc="Créez les comptes de connexion nécessaires selon l'organisation de votre entreprise. Attribuez à chaque compte les droits d'accès adaptés à son rôle. Pour les postes de pointage, nous recommandons des comptes dédiés par site plutôt que des comptes nominatifs."
              module="Paramètres"
            />
            <ConfigStep
              num="4"
              title="Ajouter les employés"
              desc="Enregistrez les employés de votre organisation avec leurs informations, leur département et leur numéro de badge. Les employés n'ont pas de compte de connexion à l'application — ils pointent via le kiosque de pointage."
              module="Employés"
            />
          </AccordionSection>

          {/* Section 2 — Guides disponibles */}
          <AccordionSection title="Guides disponibles dans l'application" icon={BookOpen} defaultOpen={true}>

            {/* Principe des guides */}
            <div
              className="p-4 rounded-xl border"
              style={{ backgroundColor: COLOR_LIGHT, borderColor: COLOR_BORDER }}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: COLOR }}>
                  <BookOpen size={15} className="text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800 mb-1">Comment accéder aux guides</p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Les modules qui nécessitent une compréhension approfondie disposent d'un <span className="font-semibold text-gray-700">bouton Guide en haut à droite</span> de leur interface. Ce guide explique en détail chaque fonctionnalité, indicateur et option de configuration du module concerné.
                  </p>
                </div>
              </div>
            </div>

            {/* Guides de configuration */}
            <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30">
              <p className="text-xs font-semibold text-gray-700 mb-2">Guides de configuration</p>
              <p className="text-xs text-gray-500 leading-relaxed mb-2">
                Certains modules nécessitent une configuration précise avant utilisation. Un guide dédié vous accompagne dans chaque choix de paramétrage et en explique les impacts sur le fonctionnement de l'application.
              </p>
              <p className="text-xs text-gray-400 italic">
                Exemple : les paramètres de pointage, la configuration des comptes utilisateurs, les modèles de documents, etc.
              </p>
            </div>

            {/* Guides de lecture */}
            <div className="p-4 rounded-xl border border-gray-100 bg-gray-50/30">
              <p className="text-xs font-semibold text-gray-700 mb-2">Guides de lecture</p>
              <p className="text-xs text-gray-500 leading-relaxed mb-2">
                D'autres modules affichent des données et des indicateurs qui peuvent nécessiter une explication pour être correctement interprétés. Le guide de lecture associé détaille la signification de chaque chiffre, statut et filtre disponible.
              </p>
              <p className="text-xs text-gray-400 italic">
                Exemple : l'historique des pointages, les rapports statistiques, les vues de gestion, etc.
              </p>
            </div>

            {/* Modules simples */}
            <div
              className="p-4 rounded-xl border"
              style={{ backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-green-100">
                  <GraduationCap size={15} className="text-green-700" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800 mb-1">Modules simples d'utilisation</p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Certains modules sont conçus pour être intuitifs et ne nécessitent pas de guide dédié. Leur prise en main est couverte lors de la <span className="font-semibold text-gray-700">formation incluse à votre abonnement HoraPilot</span>. Si vous avez des questions sur ces modules, n'hésitez pas à contacter votre référent DANICED GROUP.
                  </p>
                </div>
              </div>
            </div>

          </AccordionSection>

          {/* Support */}
          <div className="px-5 py-4 rounded-xl border border-gray-200 bg-white">
            <p className="text-xs font-semibold text-gray-700 mb-1">Besoin d'aide supplémentaire ?</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              Pour toute question non couverte par les guides, ou pour toute opération sensible sur votre compte ou votre licence, contactez le support DANICED GROUP.
            </p>
            <p className="text-xs font-semibold mt-2" style={{ color: COLOR }}>
              support@danicedgroup.com
            </p>
          </div>

        </div>

        {/* FOOTER */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-between">
          <p className="text-xs text-gray-400">
            HoraPilot by <span className="font-semibold text-gray-600">DANICED GROUP</span>
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg font-semibold text-xs border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
          >
            Fermer
          </button>
        </div>
      </div>
    </>,
    document.body
  );
};

export default AideGlobale;